import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { GearSix, Bank, FloppyDisk } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CompanySettings() {
  const { t } = useLanguage();
  const [bankForm, setBankForm] = useState({
    bank_name: '', bank_iban: '', bank_account_name: '',
    bank_account_number: '', bank_swift: '', bank_phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/company-settings`);
      setBankForm({
        bank_name: res.data.bank_name || '',
        bank_iban: res.data.bank_iban || '',
        bank_account_name: res.data.bank_account_name || '',
        bank_account_number: res.data.bank_account_number || '',
        bank_swift: res.data.bank_swift || '',
        bank_phone: res.data.bank_phone || '',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/company-settings`, bankForm);
      toast.success(t('changesSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64 text-zinc-500">{t('loading')}</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-2xl" data-testid="company-settings-page">
        <div className="flex items-center gap-3 mb-8">
          <GearSix size={28} weight="fill" className="text-zinc-700" />
          <div>
            <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('companySettings')}
            </h1>
            <p className="text-sm text-zinc-500">{t('companySettingsDesc')}</p>
          </div>
        </div>

        <form onSubmit={handleSave}>
          {/* Bank Details Card */}
          <div className="bg-white border border-zinc-200 mb-6" data-testid="company-bank-details-card">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
              <Bank size={20} weight="fill" className="text-blue-600" />
              <h2 className="font-bold text-base" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('bankDetails')}
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-zinc-400 border-l-2 border-zinc-300 pl-3 mb-4">
                {t('bankDetailsGlobalHint')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">{t('bankName')}</Label>
                  <Input
                    value={bankForm.bank_name}
                    onChange={e => setBankForm({ ...bankForm, bank_name: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="e.g. Bank of Cyprus"
                    data-testid="company-bank-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('bankAccountName')}</Label>
                  <Input
                    value={bankForm.bank_account_name}
                    onChange={e => setBankForm({ ...bankForm, bank_account_name: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="e.g. CleanPro Ltd"
                    data-testid="company-bank-account-name"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('bankIban')}</Label>
                <Input
                  value={bankForm.bank_iban}
                  onChange={e => setBankForm({ ...bankForm, bank_iban: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  placeholder="e.g. CY17 0020 0128 0000 0012 0052 7600"
                  data-testid="company-bank-iban"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">{t('bankAccountNumber')}</Label>
                  <Input
                    value={bankForm.bank_account_number}
                    onChange={e => setBankForm({ ...bankForm, bank_account_number: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    data-testid="company-bank-account-number"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('bankSwift')}</Label>
                  <Input
                    value={bankForm.bank_swift}
                    onChange={e => setBankForm({ ...bankForm, bank_swift: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    placeholder="e.g. BCYPCY2N"
                    data-testid="company-bank-swift"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('bankPhone')}</Label>
                <Input
                  value={bankForm.bank_phone}
                  onChange={e => setBankForm({ ...bankForm, bank_phone: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  data-testid="company-bank-phone"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-zinc-950 rounded-none px-8" data-testid="save-company-settings">
              <FloppyDisk size={18} className="mr-2" />
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
